import { useState } from 'react';

const SERVER_URL = process.env.REACT_APP_API_HOST || 'http://localhost:8080';

interface QbSyncResult {
  ok: boolean;
  message: string;
  qbEstimateId?: string;
  status?: string;
  emailStatus?: string;
}

interface QbPollOpenUpdate {
  quoteId: number;
  previousStatus: string;
  newStatus: string;
  qbStatus: string;
}

interface QbPollOpenResult {
  ok: boolean;
  message: string;
  updates?: QbPollOpenUpdate[];
}

// Shared client for the /qb/estimate and /qb/estimate-status routes, used by
// both the quote list (per-row icon actions) and the quote detail page.
export function useQuickBooksSync() {
  const [pendingIds, setPendingIds] = useState<Set<number>>(new Set());
  const [isPollingOpen, setIsPollingOpen] = useState(false);

  const setPending = (quoteId: number, pending: boolean) => {
    setPendingIds((prev) => {
      const next = new Set(prev);
      if (pending) next.add(quoteId);
      else next.delete(quoteId);
      return next;
    });
  };

  const isPending = (quoteId: number) => pendingIds.has(quoteId);

  const sendToQuickBooks = async (quoteId: number): Promise<QbSyncResult> => {
    setPending(quoteId, true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${SERVER_URL}/qb/estimate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ quoteId }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || 'QuickBooks sync failed');
      return {
        ok: true,
        message: `Created QuickBooks estimate ${body.docNumber} ($${Number(body.total).toFixed(2)})`,
        qbEstimateId: String(body.estimateId),
      };
    } catch (err: any) {
      return { ok: false, message: err.message || 'QuickBooks sync failed' };
    } finally {
      setPending(quoteId, false);
    }
  };

  const checkQuickBooksStatus = async (quoteId: number): Promise<QbSyncResult> => {
    setPending(quoteId, true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${SERVER_URL}/qb/estimate-status?quoteId=${quoteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || 'QuickBooks status check failed');
      return {
        ok: true,
        message: body.changed
          ? `QuickBooks status is "${body.qbStatus}" — quote updated to "${body.status}"`
          : `QuickBooks status is "${body.qbStatus || 'unchanged'}" — no update needed`,
        status: body.status,
        emailStatus: body.emailStatus,
      };
    } catch (err: any) {
      return { ok: false, message: err.message || 'QuickBooks status check failed' };
    } finally {
      setPending(quoteId, false);
    }
  };

  const pollOpenQuotes = async (): Promise<QbPollOpenResult> => {
    setIsPollingOpen(true);
    try {
      const token = localStorage.getItem('authToken');
      const resp = await fetch(`${SERVER_URL}/qb/estimate-status/poll-open`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.error || 'QuickBooks batch status check failed');

      const { checked, updated, updates, emailUpdated, chunkErrors, skipped } = body;
      let message: string;
      if (checked === 0) {
        message = 'No open quotes to check';
      } else {
        message = `Checked ${checked}, ${updated} updated`;
        if (emailUpdated > 0) {
          message += `, ${emailUpdated} email status update${emailUpdated === 1 ? '' : 's'}`;
        }
        if (chunkErrors?.length > 0) {
          message += ` (${chunkErrors.length} batch(es) failed — try again)`;
        }
        if (skipped > 0) {
          message += ` — ${skipped} older open quote${skipped === 1 ? '' : 's'} not checked this round`;
        }
      }

      return { ok: !(chunkErrors?.length > 0), message, updates };
    } catch (err: any) {
      return { ok: false, message: err.message || 'QuickBooks batch status check failed' };
    } finally {
      setIsPollingOpen(false);
    }
  };

  return { sendToQuickBooks, checkQuickBooksStatus, pollOpenQuotes, isPending, isPollingOpen };
}
